// ============================================
// components/salon/SalonSidebar.js
// Salon-specific sidebar with all salon modules
// ============================================

import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home, BarChart2, Settings, LogOut, Wallet,
  ChevronRight, Scissors, Users, 
  ChevronDown, ChevronUp, Sun, Moon, Menu, History,
  Package, CreditCard, UserCheck, TrendingUp, Megaphone, UserPlus,
  Lock,
} from "lucide-react";
import { toast } from 'react-toastify';
import { useAuth } from '../../common/context/AuthContext';
import { SALON_API_URL } from '../../api/config';

const SalonSidebar = ({ isMobileOpen: isMobileOpenProp, setIsMobileOpen: setIsMobileOpenProp, onCollapseChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, userData, userToken: token, hasSalonPermission } = useAuth();

  const [salonName, setSalonName] = useState('');
  const [salonLogoUrl, setSalonLogoUrl] = useState('');
  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(
    () => localStorage.getItem("salonSidebarCollapsed") === "true"
  );
  const [isDarkTheme, setIsDarkTheme] = useState(() => {
    return localStorage.getItem("theme") === "dark";
  });

  useEffect(() => {
    localStorage.setItem("theme", isDarkTheme ? "dark" : "light");
    document.documentElement.classList.toggle("dark", isDarkTheme);
  }, [isDarkTheme]);

  useEffect(() => {
    localStorage.setItem("salonSidebarCollapsed", isCollapsed.toString());
    onCollapseChange?.(isCollapsed);
  }, [isCollapsed, onCollapseChange]);

  useEffect(() => {
    let cancelled = false;
    const loadSalonMeta = async () => {
      if (!token) return;
      try {
        const res = await fetch(SALON_API_URL + '/admin-settings', {
          headers: { Authorization: 'Bearer ' + token },
        });
        const data = await res.json();
        if (!cancelled && data.success) {
          setSalonName(data.data?.salonProfile?.salonName || '');
          setSalonLogoUrl(data.data?.salonProfile?.logoUrl || '');
        }
      } catch {}
    };

    loadSalonMeta();
    return () => { cancelled = true; };
  }, [token]);

  const displaySalonName = salonName || userData?.salonName || 'My Salon';

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/salon/signin");
    } catch (error) {
      navigate("/salon/signin");
    }
  };

  const isActive = (path) => location.pathname === path;

  const navItemClass = (path) =>
    `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
      isActive(path)
        ? "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-semibold"
        : isDarkTheme
          ? "text-slate-400 hover:bg-slate-800"
          : "text-slate-500 hover:bg-slate-100"
    }`;

  const subNavItemClass = (path) =>
    `flex items-center gap-3 pl-12 pr-4 py-2 rounded-lg transition-colors text-xs font-medium ${
      isActive(path)
        ? "text-purple-600 dark:text-purple-300 font-bold"
        : isDarkTheme
          ? "text-slate-400 hover:bg-slate-800"
          : "text-slate-500 hover:bg-slate-100"
    }`;

  const SectionTitle = ({ children }) =>
    !isCollapsed ? (
      <div className="text-[10px] font-bold text-slate-400 tracking-wider uppercase px-4 mt-6 mb-2">
        {children}
      </div>
    ) : null;

  const NavItem = ({ path, icon: Icon, name, permission }) => {
    const hasAccess = !permission || hasSalonPermission(permission);
    if (!hasAccess) {
      return (
        <div
          onClick={() => {
            toast.warning(`Access Denied: "${name}" is locked. Owner permission required.`);
          }}
          className="flex items-center justify-between px-4 py-2.5 rounded-lg transition-colors cursor-not-allowed opacity-60 hover:bg-slate-105 dark:hover:bg-slate-800/40"
        >
          <div className="flex items-center">
            <Icon size={20} className="text-slate-400" />
            {!isCollapsed && <span className="ml-3 text-sm text-slate-405">{name}</span>}
          </div>
          {!isCollapsed && <Lock size={14} className="text-slate-400 shrink-0 ml-2" />}
        </div>
      );
    }
    return (
      <Link
        to={path}
        onClick={() => { if (window.innerWidth < 1024) setIsMobileOpen(false); }}
        className={navItemClass(path)}
      >
        <Icon size={20} />
        {!isCollapsed && <span className="ml-3 text-sm">{name}</span>}
      </Link>
    );
  };

  const reportsSubItems = [
    { path: "/salon/sales-report",     icon: TrendingUp,  name: "Sales Report", key: "sales" },
    { path: "/salon/expense-report",   icon: Wallet,      name: "Expenses Report", key: "expenses" },
    { path: "/salon/attendant-report", icon: UserCheck,   name: "Attendant Report", key: "attendant" },
  ];

  const isReportRestricted = (key) => {
    if (userData?.role !== 'manager') return false;
    const restrictedList = userData?.restrictedFeatures || [];
    if (key === 'sales') return restrictedList.includes('sales_reports');
    if (key === 'expenses') return restrictedList.includes('expense_reports');
    if (key === 'attendant') return restrictedList.includes('analytics');
    return false;
  };

  return (
    <>
      {/* Mobile hamburger button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        aria-label="Toggle menu"
      >
        {isMobileOpen ? <ChevronRight className="w-5 h-5 text-gray-700" /> : <Menu className="w-5 h-5 text-gray-700" />}
      </button>

      {/* Floating expand tab — visible on desktop when sidebar is collapsed */}
      {isCollapsed && (
        <button
          onClick={() => setIsCollapsed(false)}
          title="Expand sidebar"
          className={`hidden lg:flex fixed top-1/2 -translate-y-1/2 z-50 items-center justify-center w-5 h-10 rounded-r-lg shadow-md transition-colors
            ${isDarkTheme
              ? "bg-slate-700 hover:bg-slate-600 text-slate-300"
              : "bg-white hover:bg-purple-50 text-slate-500 border border-slate-200"
            }`}
          style={{ left: '56px' /* w-14 = 3.5rem = 56px */ }}
        >
          <ChevronRight size={12} />
        </button>
      )}

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden" onClick={() => setIsMobileOpen(false)} />
      )}

      <aside
        className={`fixed top-0 left-0 h-screen z-40 transition-all border-r flex flex-col
          ${isDarkTheme ? "bg-slate-900 border-slate-800 text-slate-200" : "bg-white border-slate-200 text-slate-900"}
          ${isCollapsed ? "w-14" : "w-64"}
          ${isMobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      >
        {/* Brand */}
        <div className="p-6 flex items-center gap-3">
          {!isCollapsed ? (
            <>
              <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 bg-purple-50 dark:bg-slate-800">
                {salonLogoUrl ? (
                  <img src={salonLogoUrl} alt={displaySalonName} className="w-full h-full object-cover" />
                ) : (
                  <img src="/Tobo.png" alt="TOBO" className="w-full h-full object-cover" />
                )}
              </div>
              <div className="flex-1">
                <h1 className={`font-bold leading-tight ${isDarkTheme ? "text-slate-100" : "text-slate-900"}`}>
                  {displaySalonName}
                </h1>
                <p className="text-xs text-slate-400 font-medium">Salon Management</p>
              </div>
              <button
                onClick={() => setIsDarkTheme(!isDarkTheme)}
                className={`transition-colors ${isDarkTheme ? "text-slate-400 hover:text-slate-300" : "text-slate-400 hover:text-slate-600"}`}
              >
                {isDarkTheme ? <Sun size={18} /> : <Moon size={18} />}
              </button>
            </>
          ) : (
            <div className="flex flex-col items-center gap-2 w-full">
              <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-purple-50 dark:bg-slate-800">
                {salonLogoUrl ? (
                  <img src={salonLogoUrl} alt={displaySalonName} className="w-full h-full object-cover" />
                ) : (
                  <img src="/Tobo.png" alt="TOBO" className="w-full h-full object-cover" />
                )}
              </div>
              <button onClick={() => setIsDarkTheme(!isDarkTheme)} className="text-slate-400">
                {isDarkTheme ? <Sun size={16} /> : <Moon size={16} />}
              </button>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 pb-8 overflow-y-auto">
          <SectionTitle>Main</SectionTitle>
          <div className="space-y-1">
            <NavItem path="/salon/dashboard"  icon={Home}       name="Dashboard" permission="dashboard.view" />
            <NavItem path="/salon/billing"    icon={CreditCard} name="Billing" permission="billing.view" />
            <NavItem path="/salon/services"   icon={Scissors}   name="Services" permission="services.view" />
            <NavItem path="/salon/products"   icon={Package}    name="Products" permission="products.view" />
            <NavItem path="/salon/memberships" icon={UserPlus}   name="Memberships" permission="memberships.view" />
            <NavItem path="/salon/expenses"   icon={Wallet}     name="Expenses" permission="expenses.view" />
          </div>

          <SectionTitle>Management</SectionTitle>
          <div className="space-y-1">
            <NavItem path="/salon/leads"        icon={UserPlus} name="Leads" permission="leads.view" />
            <NavItem path="/salon/staff"        icon={Users}    name="Staff Management" permission="staff.view" />
            <NavItem path="/salon/bill-history" icon={History}  name="Bill History" permission="bill_history.view" />
            <NavItem path="/salon/marketing"    icon={Megaphone} name="Marketing" permission="marketing.view" />
          </div>

          {/* Reports */}
          <SectionTitle>Reports</SectionTitle>
          <div className="space-y-1">
            {hasSalonPermission('reports.view') ? (
              <button
                onClick={() => setIsReportsOpen(!isReportsOpen)}
                className={`flex items-center justify-between w-full px-4 py-2.5 rounded-lg transition-colors ${
                  reportsSubItems.some(i => isActive(i.path))
                    ? "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-semibold"
                    : isDarkTheme
                      ? "text-slate-400 hover:bg-slate-800"
                      : "text-slate-500 hover:bg-slate-100"
                }`}
              >
                <div className="flex items-center">
                  <BarChart2 size={20} />
                  {!isCollapsed && <span className="ml-3 text-sm">Reports</span>}
                </div>
                {!isCollapsed && (isReportsOpen ? <ChevronUp size={14} className="opacity-50" /> : <ChevronDown size={14} className="opacity-50" />)}
              </button>
            ) : (
              <div
                onClick={() => {
                  toast.warning('Access Denied: Reports are locked. Owner permission required.');
                }}
                className="flex items-center justify-between w-full px-4 py-2.5 rounded-lg transition-colors cursor-not-allowed opacity-60 hover:bg-slate-100 dark:hover:bg-slate-800/40"
              >
                <div className="flex items-center">
                  <BarChart2 size={20} className="text-slate-400" />
                  {!isCollapsed && <span className="ml-3 text-sm text-slate-400">Reports</span>}
                </div>
                {!isCollapsed && <Lock size={14} className="text-slate-400 shrink-0 ml-2" />}
              </div>
            )}

            {hasSalonPermission('reports.view') && isReportsOpen && !isCollapsed && (
              <div className="mt-1 space-y-1">
                {reportsSubItems.map(({ path, icon: Icon, name, key }) => {
                  const locked = isReportRestricted(key);
                  if (locked) {
                    return (
                      <div
                        key={path}
                        onClick={() => {
                          toast.warning(`Access Denied: "${name}" is locked. Owner permission required.`);
                        }}
                        className="flex items-center justify-between pl-12 pr-4 py-2 rounded-lg transition-colors cursor-not-allowed opacity-60 hover:bg-slate-100 dark:hover:bg-slate-800/40 text-xs text-slate-400"
                      >
                        <div className="flex items-center gap-3">
                          <Icon size={14} className="text-slate-400" />
                          <span>{name}</span>
                        </div>
                        <Lock size={12} className="text-slate-405 shrink-0 ml-2" />
                      </div>
                    );
                  }
                  return (
                    <Link key={path} to={path}
                      onClick={() => { if (window.innerWidth < 1024) setIsMobileOpen(false); }}
                      className={subNavItemClass(path)}
                    >
                      <Icon size={14} />
                      <span>{name}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          <SectionTitle>Settings</SectionTitle>
          <div className="space-y-1">
            <NavItem path="/salon/settings" icon={Settings} name="Settings" permission="settings.view" />
          </div>
        </nav>

        {/* Collapse toggle — always visible on desktop */}
        <div className={`hidden lg:block px-4 pb-2 border-t ${isDarkTheme ? "border-slate-800" : "border-slate-100"}`}>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={`flex items-center justify-center w-full px-4 py-2 mt-2 text-sm rounded-lg transition-colors ${
              isDarkTheme ? "text-slate-400 hover:bg-slate-800" : "text-slate-500 hover:bg-slate-100"
            }`}
          >
            {!isCollapsed && <span className="mr-2">Collapse</span>}
            <ChevronRight size={16} className={`transition-transform duration-300 ${isCollapsed ? "" : "rotate-180"}`} />
          </button>
        </div>

        {/* Lock Admin Section */}
        <div className={`px-4 border-t ${isDarkTheme ? "border-slate-800" : "border-slate-100"} pt-2`}>
          <button
            onClick={() => {
              localStorage.removeItem('salonAdminPinUnlockedTime');
              sessionStorage.removeItem('salonAdminPinUnlockedTime');
              localStorage.removeItem('salon_report_access_sales');
              sessionStorage.removeItem('salon_report_access_sales');
              localStorage.removeItem('salon_report_access_expenses');
              sessionStorage.removeItem('salon_report_access_expenses');
              localStorage.removeItem('salon_report_access_attendant');
              sessionStorage.removeItem('salon_report_access_attendant');
              toast.success('Admin PIN session locked permanently.');
              setTimeout(() => {
                window.location.reload();
              }, 600);
            }}
            className={`flex items-center gap-3 w-full px-4 py-3 text-amber-500 hover:text-amber-600 transition-colors text-sm font-medium rounded-lg ${isDarkTheme ? "hover:bg-amber-900/20" : "hover:bg-amber-50"}`}
          >
            <Lock size={20} />
            {!isCollapsed && <span>Lock Session</span>}
          </button>
        </div>

        {/* Logout */}
        <div className={`p-4 border-t ${isDarkTheme ? "border-slate-800" : "border-slate-100"}`}>
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 w-full px-4 py-3 text-red-500 hover:text-red-600 transition-colors text-sm font-medium rounded-lg ${isDarkTheme ? "hover:bg-red-900/20" : "hover:bg-red-50"}`}
          >
            <LogOut size={20} />
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default SalonSidebar;
